import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { ApiError, api } from '../api/client.js'
import type { ExportDataset, ExportMeta, ExportPreset, ExportPreview, Template } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { ObjectPicker } from '../components/objects.js'
import { AssigneePicker, type Assignment } from '../components/people.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Field, Modal, Notice, Spinner } from '../components/ui.js'
import { addDays, addMonths, endOfMonth, formatDate, startOfMonth, todayIso } from '../lib/format.js'
import { PageHead } from './Layout.js'

interface State {
  presetId: string
  title: string
  dataset: ExportDataset
  columns: string[]
  status: string[]
  withExternalRef: boolean
  from: string
  to: string
  objectId: string
  templateId: string
  assignment: Assignment
}

function lastMonth(): { from: string; to: string } {
  const first = startOfMonth(addMonths(todayIso(), -1))
  return { from: first, to: endOfMonth(first) }
}

const RANGES: { label: string; range: () => { from: string; to: string } }[] = [
  { label: 'Letzte 7 Tage', range: () => ({ from: addDays(todayIso(), -6), to: todayIso() }) },
  { label: 'Dieser Monat', range: () => ({ from: startOfMonth(todayIso()), to: todayIso() }) },
  { label: 'Letzter Monat', range: lastMonth },
  { label: 'Letzte 90 Tage', range: () => ({ from: addDays(todayIso(), -89), to: todayIso() }) },
]

function toParams(state: State, extra: Record<string, string> = {}): URLSearchParams {
  const p = new URLSearchParams({ dataset: state.dataset, from: state.from, to: state.to, ...extra })
  if (state.columns.length) p.set('columns', state.columns.join(','))
  if (state.status.length) p.set('status', state.status.join(','))
  if (state.objectId) p.set('objectId', state.objectId)
  if (state.dataset === 'tasks') {
    if (state.templateId) p.set('templateId', state.templateId)
    if (state.assignment.assigneeId) p.set('personId', state.assignment.assigneeId)
    if (state.assignment.assigneeTeamId) p.set('teamId', state.assignment.assigneeTeamId)
    if (state.withExternalRef) p.set('withExternalRef', 'true')
  }
  if (state.title) p.set('title', state.title)
  return p
}

async function download(url: string): Promise<void> {
  const response = await fetch(url, { credentials: 'same-origin' })
  if (!response.ok) {
    let message = `Fehler ${response.status}`
    try {
      message = ((await response.json()) as { message?: string }).message ?? message
    } catch {
      /* not JSON */
    }
    throw new Error(message)
  }
  const disposition = response.headers.get('content-disposition') ?? ''
  const name = decodeURIComponent(/filename\*=UTF-8''([^;]+)/.exec(disposition)?.[1] ?? 'export')
  const blob = await response.blob()
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = name
  document.body.append(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(link.href), 10_000)
}

/**
 * Auswertungen: the lists the office used to build from the 30-column
 * Hausmanager report with a macro, available directly as a tidy Excel file.
 * A preset picks columns and statuses; everything is adjustable and can be
 * saved as the office's own Vorlage.
 */
export function ExportsPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const meta = useQuery({ queryKey: ['export-meta'], queryFn: () => api<ExportMeta>('/api/exports/meta') })
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api<Template[]>('/api/templates') })
  const [state, setState] = useState<State | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  function applyPreset(preset: ExportPreset, keepRange = true) {
    setState((current) => ({
      presetId: preset.id,
      title: preset.name,
      dataset: preset.dataset,
      columns: preset.columns,
      status: preset.status,
      withExternalRef: Boolean(preset.withExternalRef),
      from: keepRange && current ? current.from : lastMonth().from,
      to: keepRange && current ? current.to : lastMonth().to,
      objectId: current?.objectId ?? '',
      templateId: preset.dataset === 'tasks' ? (current?.templateId ?? '') : '',
      assignment: current?.assignment ?? { assigneeId: null, assigneeTeamId: null },
    }))
  }

  useEffect(() => {
    if (meta.data && !state) applyPreset(meta.data.presets[0]!, false)
  }, [meta.data]) // eslint-disable-line react-hooks/exhaustive-deps

  const params = state ? toParams(state).toString() : ''
  const preview = useQuery({
    queryKey: ['export-preview', params],
    queryFn: () => api<ExportPreview>(`/api/exports/preview?${params}`),
    enabled: Boolean(state) && state!.to >= state!.from,
    placeholderData: (previous) => previous,
  })

  const deleteProfile = useMutation({
    mutationFn: (id: string) => api(`/api/exports/profiles/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      toast('Vorlage gelöscht.')
      await queryClient.invalidateQueries({ queryKey: ['export-meta'] })
    },
  })

  const columnLabels = useMemo(() => new Map((meta.data?.columns[state?.dataset ?? 'tasks'] ?? []).map((c) => [c.key, c.label])), [meta.data, state?.dataset])

  if (meta.isLoading || !meta.data || !state) return <Spinner />
  const m = meta.data
  const available = m.columns[state.dataset].filter((c) => !state.columns.includes(c.key))
  const statuses = m.statuses[state.dataset]

  async function run(kind: 'xlsx' | 'csv' | 'photos') {
    if (!state) return
    setBusy(kind)
    try {
      const url = kind === 'photos' ? `/api/exports/photos?${toParams(state)}` : `/api/exports/file?${toParams(state, { format: kind })}`
      await download(url)
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Download fehlgeschlagen.', 'error')
    } finally {
      setBusy(null)
    }
  }

  function moveColumn(key: string, delta: number) {
    if (!state) return
    const i = state.columns.indexOf(key)
    const j = i + delta
    if (j < 0 || j >= state.columns.length) return
    const next = [...state.columns]
    ;[next[i], next[j]] = [next[j]!, next[i]!]
    setState({ ...state, columns: next, presetId: '' })
  }

  const summary = preview.data?.summary ?? {}
  const done = summary.done ?? 0
  const notDone = (summary.missed ?? 0) + (summary.final_missed ?? 0)

  return (
    <>
      <PageHead
        title="Auswertungen"
        subtitle="Rückmeldung an Eigentümer, Abrechnung, Mängelauszug: direkt als Excel, ohne Makro."
      />

      <div className="export-layout">
        <aside className="card export-presets">
          <div className="card-head">
            <h2>Vorlagen</h2>
          </div>
          <ul>
            {m.presets.map((p) => (
              <li key={p.id}>
                <button type="button" className={`preset ${state.presetId === p.id ? 'on' : ''}`} onClick={() => applyPreset(p)}>
                  <span className="strong">{p.name}</span>
                  {p.hint && <span className="muted small">{p.hint}</span>}
                </button>
              </li>
            ))}
          </ul>
          {m.profiles.length > 0 && (
            <>
              <div className="card-head sub">
                <h3>Eigene Vorlagen</h3>
              </div>
              <ul>
                {m.profiles.map((p) => (
                  <li key={p.id} className="with-action">
                    <button type="button" className={`preset ${state.presetId === p.id ? 'on' : ''}`} onClick={() => applyPreset(p)}>
                      <span className="strong">{p.name}</span>
                      <span className="muted small">{p.dataset === 'issues' ? 'Mängel und Tickets' : 'Aufgaben'}, {p.columns.length} Spalten</span>
                    </button>
                    <button
                      type="button"
                      className="btn ghost small"
                      aria-label={`Vorlage ${p.name} löschen`}
                      onClick={() => {
                        if (window.confirm(`Vorlage „${p.name}“ löschen?`)) deleteProfile.mutate(p.id)
                      }}
                    >
                      <Icon name="close" size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </aside>

        <div className="export-main">
          <section className="card" style={{ marginBottom: 16 }}>
            <div className="card-body">
              <div className="export-filters">
                <Field label="Von">
                  <input type="date" value={state.from} onChange={(e) => setState({ ...state, from: e.target.value })} />
                </Field>
                <Field label="Bis">
                  <input type="date" value={state.to} min={state.from} onChange={(e) => setState({ ...state, to: e.target.value })} />
                </Field>
                <div className="quick-range">
                  {RANGES.map((r) => (
                    <button key={r.label} type="button" className="btn secondary small" onClick={() => setState({ ...state, ...r.range() })}>
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="export-filters">
                <Field label="Kunde, Liegenschaft oder Objekt">
                  <ObjectPicker value={state.objectId} taskable={false} onChange={(id) => setState({ ...state, objectId: id })} placeholder="Alle, zum Eingrenzen suchen" />
                </Field>
                {state.dataset === 'tasks' && (
                  <>
                    <Field label="Leistung">
                      <select value={state.templateId} onChange={(e) => setState({ ...state, templateId: e.target.value })}>
                        <option value="">Alle</option>
                        {templates.data?.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Zuständig oder ausgeführt von">
                      <AssigneePicker value={state.assignment} onChange={(assignment) => setState({ ...state, assignment })} emptyLabel="Alle" />
                    </Field>
                  </>
                )}
              </div>
              <div className="export-filters wrap">
                <div className="field">
                  <span>Status</span>
                  <div className="chips">
                    {statuses.map((s) => {
                      const on = state.status.includes(s.value)
                      return (
                        <button
                          key={s.value}
                          type="button"
                          className={`chip chip-btn ${on ? 'on' : ''}`}
                          aria-pressed={on}
                          onClick={() =>
                            setState({ ...state, presetId: '', status: on ? state.status.filter((x) => x !== s.value) : [...state.status, s.value] })
                          }
                        >
                          {s.label}
                        </button>
                      )
                    })}
                    {state.status.length === 0 && <span className="muted small">alle</span>}
                  </div>
                </div>
                {state.dataset === 'tasks' && (
                  <label className="switch-inline">
                    <input type="checkbox" checked={state.withExternalRef} onChange={(e) => setState({ ...state, withExternalRef: e.target.checked })} />
                    Nur Aufgaben mit Youtilly-ID
                  </label>
                )}
              </div>

              <div className="field">
                <span>Spalten in dieser Reihenfolge</span>
                <div className="column-chips">
                  {state.columns.map((key, index) => (
                    <span key={key} className="column-chip">
                      <button type="button" className="col-btn" aria-label="nach links" disabled={index === 0} onClick={() => moveColumn(key, -1)}>
                        <Icon name="chevronLeft" size={14} />
                      </button>
                      {columnLabels.get(key) ?? key}
                      <button type="button" className="col-btn" aria-label="nach rechts" disabled={index === state.columns.length - 1} onClick={() => moveColumn(key, 1)}>
                        <Icon name="chevronRight" size={14} />
                      </button>
                      <button
                        type="button"
                        className="col-btn"
                        aria-label={`${columnLabels.get(key)} entfernen`}
                        onClick={() => setState({ ...state, presetId: '', columns: state.columns.filter((c) => c !== key) })}
                      >
                        <Icon name="close" size={13} />
                      </button>
                    </span>
                  ))}
                  {available.length > 0 && (
                    <select
                      className="select-inline"
                      value=""
                      onChange={(e) => e.target.value && setState({ ...state, presetId: '', columns: [...state.columns, e.target.value] })}
                      aria-label="Spalte hinzufügen"
                    >
                      <option value="">+ Spalte hinzufügen</option>
                      {available.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>
            <footer className="export-actions">
              <div className="muted small">
                {preview.data ? (
                  <>
                    <strong>{preview.data.total}</strong> Zeilen
                    {state.dataset === 'tasks' && preview.data.total > 0 && (
                      <>
                        , davon {done} erledigt und {notDone} nicht erledigt
                        {done + notDone > 0 && <>, Quote {Math.round((done / (done + notDone)) * 100)} %</>}
                      </>
                    )}
                    {preview.isFetching ? ', wird aktualisiert' : ''}
                  </>
                ) : (
                  'Wird berechnet …'
                )}
              </div>
              <div className="actions">
                <button type="button" className="btn secondary" onClick={() => setSaving(true)} disabled={state.columns.length === 0}>
                  Als Vorlage speichern
                </button>
                {state.dataset === 'tasks' && (
                  <button type="button" className="btn secondary" onClick={() => void run('photos')} disabled={busy !== null}>
                    <Icon name="download" /> {busy === 'photos' ? 'Wird gepackt …' : 'Fotos als ZIP'}
                  </button>
                )}
                <button type="button" className="btn secondary" onClick={() => void run('csv')} disabled={busy !== null}>
                  CSV
                </button>
                <button type="button" className="btn" onClick={() => void run('xlsx')} disabled={busy !== null || !preview.data?.total}>
                  <Icon name="download" /> {busy === 'xlsx' ? 'Wird erstellt …' : 'Excel herunterladen'}
                </button>
              </div>
            </footer>
          </section>

          <section className="card">
            {preview.error ? (
              <Notice kind="error">{preview.error instanceof ApiError ? preview.error.message : 'Vorschau fehlgeschlagen.'}</Notice>
            ) : !preview.data ? (
              <Spinner />
            ) : preview.data.total === 0 ? (
              <EmptyState title="Keine Zeilen für diese Auswahl" hint={`Zeitraum ${formatDate(state.from)} bis ${formatDate(state.to)}. Filter oder Zeitraum anpassen.`} />
            ) : (
              <>
                <div className="table-scroll export-preview">
                  <table className="data">
                    <thead>
                      <tr>
                        {preview.data.columns.map((c) => (
                          <th key={c.key}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.data.rows.map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j} className="small">
                              {cell ?? ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {preview.data.total > preview.data.rows.length && (
                  <div className="muted small" style={{ padding: '10px 16px' }}>
                    Vorschau zeigt die ersten {preview.data.rows.length} von {preview.data.total} Zeilen. Die Datei enthält alle.
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </div>

      {saving && (
        <SaveProfileModal
          state={state}
          onClose={() => setSaving(false)}
          onSaved={async (id) => {
            setSaving(false)
            await queryClient.invalidateQueries({ queryKey: ['export-meta'] })
            setState({ ...state, presetId: id })
          }}
        />
      )}
    </>
  )
}

function SaveProfileModal({ state, onClose, onSaved }: { state: State; onClose: () => void; onSaved: (id: string) => void }) {
  const toast = useToast()
  const [name, setName] = useState(state.presetId ? `${state.title} (eigene)` : '')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/api/exports/profiles', {
        method: 'POST',
        body: { name, dataset: state.dataset, columns: state.columns, status: state.status, withExternalRef: state.withExternalRef },
      }),
    onSuccess: (result) => {
      toast('Vorlage gespeichert.')
      onSaved(result.id)
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })
  return (
    <Modal
      open
      title="Als Vorlage speichern"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="btn" disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>
            Speichern
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      <Field label="Name" hint="Gespeichert werden Spalten, Reihenfolge und Status. Zeitraum und Objekt wählen Sie jedes Mal neu.">
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="z. B. LEG Legionellen monatlich" />
      </Field>
    </Modal>
  )
}
